import platform from 'platform';

export type PermissionDialogVariant =
  | 'ios_safari'
  | 'ios_chrome'
  | 'android_chrome'
  | 'desktop_chrome'
  | 'desktop_safari'
  | 'other';

const isTouchDevice = () => {
  if ('ontouchstart' in window || window.TouchEvent) return true;

  const prefixes = ['', '-webkit-', '-moz-', '-o-', '-ms-'];
  const queries = prefixes.map((prefix) => `(${prefix}touch-enabled)`);

  return window.matchMedia(queries.join(',')).matches;
};

const isSocialMediaWebview = () => {
  const ua = navigator.userAgent || navigator.vendor;
  return (
    ua.indexOf('FBAN') > -1 ||
    ua.indexOf('FBAV') > -1 ||
    ua.indexOf('Instagram') > -1 ||
    ua.indexOf('Snapchat') > -1 ||
    ua.indexOf('Pinterest') > -1
  );
};

export const isWebRTCSupported = () => {
  const isRTCPeerConnectionSupported = () => {
    return (
      typeof window.RTCPeerConnection !== 'undefined' ||
      typeof (window as any).webkitRTCPeerConnection !== 'undefined'
    );
  };

  const isGetUserMediaSupported = () => {
    return (
      !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) ||
      !!(window as any).getUserMedia
    );
  };

  return isGetUserMediaSupported() && isRTCPeerConnectionSupported();
};

const getAbbreviatedDeviceInfo = () => {
  // Remove spaces from the os and browser names.
  const osName = (platform.os?.family || '').split(' ').join('');
  const browserName = (platform.name || '').split(' ').join('');

  const osVersion = platform.os?.version || '';
  const browserVersion = platform.version || '';
  const majorVersion = browserVersion.substring(0, browserVersion.indexOf('.'));
  return `${osName}${osVersion}|${browserName}${majorVersion}`;
};
// Safari on mobile platform.os?.family is same as safari on desktop
// so checking osname for mobile  its iOS and on desktop its OSX
// Additionally check userAgent directly since platform lib misses iOS Safari,
// and check maxTouchPoints for iPadOS 13+ which reports platform as MacIntel
const IS_IOS =
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
  /iOS/i.test(getAbbreviatedDeviceInfo()) ||
  platform.os?.family === 'iOS';
const IS_ANDROID =
  /Android/i.test(getAbbreviatedDeviceInfo()) ||
  platform.os?.family === 'Android';

const IS_BROWSER_SAFARI =
  platform.name === 'Safari' || platform.name === 'Mobile Safari';
const IS_BROWSER_CHROME =
  platform.name === 'Chrome' ||
  platform.name === 'Chrome Mobile' ||
  platform.name === 'Chrome Mobile iOS';

const PERMISSION_DIALOG_VARIANT: PermissionDialogVariant = (() => {
  if (IS_IOS && IS_BROWSER_SAFARI) return 'ios_safari';
  if (IS_IOS && IS_BROWSER_CHROME) return 'ios_chrome';
  if (IS_ANDROID && IS_BROWSER_CHROME) return 'android_chrome';
  if (!IS_IOS && !IS_ANDROID && IS_BROWSER_CHROME) return 'desktop_chrome';
  if (!IS_IOS && !IS_ANDROID && IS_BROWSER_SAFARI) return 'desktop_safari';
  return 'other';
})();

export const getPermissionBlockedDialogKeys = (otherButtonKey: string) => {
  if (PERMISSION_DIALOG_VARIANT !== 'other') {
    return {
      titleKey: 'patient_web.media_device_permission.blocked.specific.title',
      messageKey: `patient_web.media_device_permission.blocked.${PERMISSION_DIALOG_VARIANT}.message`,
      buttonKey: 'patient_web.media_device_permission.blocked.specific.button',
    };
  }

  return {
    titleKey: 'patient_web.media_device_permission.blocked.title',
    messageKey:
      IS_IOS || IS_ANDROID
        ? 'patient_web.media_device_permission.blocked.message_mobile'
        : 'patient_web.media_device_permission.blocked.message_desktop',
    buttonKey: otherButtonKey,
  };
};

const device = {
  IS_IOS,
  IS_ANDROID,
  IS_MOBILE: IS_IOS || IS_ANDROID,

  IS_SOCIAL_MEDIA_IN_APP_BROWSER: isSocialMediaWebview(),

  IS_TOUCH_ENABLED: isTouchDevice(),
  IS_WEB_RTC_CAPABLE: isWebRTCSupported(),

  BROWSER_NAME: platform.name,
  DESCRIPTION: platform.description,
  ABBREVIATED_DESCRIPTION: getAbbreviatedDeviceInfo(),
  PERMISSION_DIALOG_VARIANT,
};

export default device;
